
import React from "react";
import { format, sub } from "date-fns";
import { CalendarIcon, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export type TimeRange = {
  label: string;
  startDate: Date;
  endDate: Date;
};

type TimeRangeSelectorProps = {
  selectedRange: TimeRange;
  onRangeChange: (range: TimeRange) => void;
  className?: string;
};

export const getTimeRanges = (): TimeRange[] => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = sub(today, { days: 1 });

  return [
    {
      label: 'Today',
      startDate: today,
      endDate: now,
    },
    {
      label: 'Yesterday',
      startDate: yesterday,
      endDate: sub(today, { seconds: 1 }),
    },
    {
      label: 'Last 7 days',
      startDate: sub(now, { days: 7 }),
      endDate: now,
    },
    {
      label: 'Last 30 days',
      startDate: sub(now, { days: 30 }),
      endDate: now,
    },
    {
      label: 'Last week',
      startDate: sub(now, { weeks: 1, days: now.getDay() }),
      endDate: sub(now, { days: now.getDay() }),
    },
    {
      label: 'Last month',
      startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1),
      endDate: new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59),
    },
    {
      label: 'This week',
      startDate: sub(now, { days: now.getDay() }),
      endDate: now,
    },
    {
      label: 'This month',
      startDate: new Date(now.getFullYear(), now.getMonth(), 1),
      endDate: now,
    },
  ];
};

export const formatDateRange = (startDate: Date, endDate: Date): string => {
  if (
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getFullYear() === endDate.getFullYear()
  ) {
    return `${format(startDate, 'MMM d')} – ${format(endDate, 'MMM d')}`;
  }
  return `${format(startDate, 'MMM d')} – ${format(endDate, 'MMM d')}`;
};

const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  selectedRange,
  onRangeChange,
  className,
}) => {
  const timeRanges = getTimeRanges();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={`flex items-center justify-between gap-2 bg-background border border-input px-3 py-2 ${className}`}
        >
          <CalendarIcon className="h-4 w-4 opacity-70" />
          <span>{selectedRange.label}</span>
          <ChevronDown className="h-4 w-4 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[280px] p-0">
        <div className="max-h-[400px] overflow-auto p-1">
          {timeRanges.map((range) => (
            <DropdownMenuItem
              key={range.label}
              className={`flex flex-col items-start gap-1 p-2 ${
                selectedRange.label === range.label
                  ? "bg-primary text-primary-foreground"
                  : ""
              }`}
              onClick={() => onRangeChange(range)}
            >
              <div className="font-medium">{range.label}</div>
              <div className="text-xs opacity-70">
                {formatDateRange(range.startDate, range.endDate)}
              </div>
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default TimeRangeSelector;
